import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import ShareCard from "@/components/ShareCard";
import ShareButton from "@/components/ShareButton";
import { Share2, MapPin, Filter } from "lucide-react";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

function StoryCard({ pledge, onShare }) {
  return (
    <article data-testid={`story-${pledge.id}`} className="group relative p-7 rounded-sm border border-[var(--line)] bg-[var(--bg)] hover:border-[var(--terracotta)]/40 hover:-translate-y-0.5 transition-all duration-300">
      <button onClick={onShare} aria-label="Share story" data-testid={`story-share-${pledge.id}`}
        className="absolute top-4 right-4 text-[var(--ink-soft)] hover:text-[var(--forest)] opacity-0 group-hover:opacity-100 transition">
        <Share2 className="w-4 h-4" />
      </button>

      <div className="flex items-baseline gap-3 mb-2">
        <span className="font-serif-display text-5xl text-[var(--forest)] leading-none">{pledge.days}</span>
        <span className="font-mono-acc text-[10px] tracking-widest text-[var(--ink-soft)] uppercase">days sober</span>
      </div>

      <div className="text-sm font-medium mb-1">
        {pledge.name}
        {pledge.state && <span className="ml-2 inline-flex items-center gap-1 text-xs text-[var(--ink-soft)]"><MapPin className="w-3 h-3" />{pledge.state}</span>}
      </div>
      <div className="font-mono-acc text-[10px] text-[var(--ink-soft)] mb-4">Sober since {pledge.sobriety_date}</div>

      {pledge.note && (
        <blockquote className="border-l-2 border-[var(--terracotta)]/40 pl-4 italic text-[var(--ink-soft)] text-sm leading-relaxed">
          &ldquo;{pledge.note}&rdquo;
        </blockquote>
      )}
    </article>
  );
}

export default function Stories() {
  const [items, setItems] = useState([]);
  const [stateFilter, setStateFilter] = useState("");
  const [sort, setSort] = useState("recent");
  const [share, setShare] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (stateFilter) params.set("state", stateFilter);
    params.set("sort", sort);
    const { data } = await api.get(`/sobriety/wall?${params.toString()}`);
    setItems(data);
  }, [stateFilter, sort]);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => ({
    count: items.length,
    days: items.reduce((s, p) => s + (p.days || 0), 0),
  }), [items]);

  return (
    <div data-testid="stories-page" className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
      <div className="overline mb-4">Wall of Hope · Heroes</div>
      <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.95] mb-6">
        Every day, a quiet<br /><em className="italic text-[var(--forest)]">act of courage.</em>
      </h1>
      <p className="text-[var(--ink-soft)] text-lg max-w-2xl leading-relaxed mb-6">
        Every card below is a real person who escaped and is still sober today.
        Filter by your state and share a hero with someone who needs hope.
      </p>

      <div className="mb-10 flex flex-wrap items-center gap-3">
        <ShareButton
          testid="stories-share-page"
          title="Wall of Hope · 50 States Sober"
          text="Real people, real days sober. Someone you love might need to read this today."
        />
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-10 p-5 border border-[var(--line)] rounded-sm bg-[var(--bg-alt)]">
        <Filter className="w-4 h-4 text-[var(--ink-soft)]" />
        <select data-testid="stories-state" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}
          className="px-4 py-2 border border-[var(--line)] rounded-sm text-sm bg-white">
          <option value="">All states</option>
          {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="flex items-center gap-1 ml-auto">
          <button data-testid="stories-sort-recent" onClick={() => setSort("recent")}
            className={`text-xs px-3 py-1.5 rounded-full ${sort === "recent" ? "bg-[var(--ink)] text-white" : "text-[var(--ink-soft)] hover:bg-white"}`}>
            Most recent
          </button>
          <button data-testid="stories-sort-longest" onClick={() => setSort("longest")}
            className={`text-xs px-3 py-1.5 rounded-full ${sort === "longest" ? "bg-[var(--ink)] text-white" : "text-[var(--ink-soft)] hover:bg-white"}`}>
            Longest sober
          </button>
        </div>
        <div className="w-full md:w-auto md:ml-4 text-xs text-[var(--ink-soft)] font-mono-acc">
          {stats.count} {stats.count === 1 ? "story" : "stories"} · {stats.days.toLocaleString()} days
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-24 text-[var(--ink-soft)]">
          No stories yet for this filter. <a href="/" className="text-[var(--forest)] underline">Be the first.</a>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((p) => (
            <StoryCard key={p.id} pledge={p} onShare={() => setShare(p)} />
          ))}
        </div>
      )}

      {share && <ShareCard pledge={share} onClose={() => setShare(null)} />}
    </div>
  );
}
